import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { BarChart, Bar, CartesianGrid, Legend, LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  CalendarRange,
  Download,
  Landmark,
  Layers3,
  PieChart,
  Sparkles,
  Wallet,
} from 'lucide-react';
import { expenseAPI, invoiceAPI } from '../api/client';
import Layout from '../components/Layout';
import { getDefaultCurrency, formatCurrency } from '../utils/currency';

const reportTypes = [
  {
    key: 'income',
    title: 'Income Report',
    description: 'Revenue performance, pending collections, and overdue exposure.',
    icon: Banknote,
    accent: 'from-emerald-500 to-teal-500',
  },
  {
    key: 'expense',
    title: 'Expense Report',
    description: 'Category-level spend patterns and average expense tracking.',
    icon: Wallet,
    accent: 'from-rose-500 to-orange-500',
  },
  {
    key: 'tax',
    title: 'Tax Summary',
    description: 'Tax burden, deductible spend, and net income view.',
    icon: Landmark,
    accent: 'from-amber-500 to-yellow-500',
  },
  {
    key: 'profitloss',
    title: 'Profit & Loss',
    description: 'Revenue, expenses, and margin trend in one statement.',
    icon: Sparkles,
    accent: 'from-violet-500 to-fuchsia-500',
  },
  {
    key: 'cashflow',
    title: 'Cash Flow',
    description: 'Cash in, cash out, and net movement over time.',
    icon: Layers3,
    accent: 'from-sky-500 to-blue-600',
  },
  {
    key: 'balance',
    title: 'Balance Sheet',
    description: 'Assets, receivables, liabilities, and equity snapshot.',
    icon: PieChart,
    accent: 'from-slate-700 to-slate-900',
  },
];

const normalizeInvoices = (response) => response?.data?.data || response?.data || [];
const normalizeExpenses = (response) => response?.data?.data || response?.data || [];

const toSafeNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const formatDay = (date) => date.toISOString().split('T')[0];

const monthKeyFromDate = (value) => {
  const date = toDate(value);
  if (!date) return 'Unknown';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const buildMonthlySeries = (items, dateField, valueGetter) => {
  const monthlyMap = items.reduce((acc, item) => {
    const monthKey = monthKeyFromDate(item[dateField]);
    acc[monthKey] = (acc[monthKey] || 0) + valueGetter(item);
    return acc;
  }, {});

  return Object.entries(monthlyMap)
    .map(([month, total]) => ({ month, total }))
    .sort((a, b) => a.month.localeCompare(b.month));
};

const getDateWindow = (dateRange, customStartDate, customEndDate) => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);

  switch (dateRange) {
    case '30days':
      start.setDate(end.getDate() - 30);
      break;
    case '90days':
      start.setDate(end.getDate() - 90);
      break;
    case '6months':
      start.setMonth(end.getMonth() - 6);
      break;
    case '1year':
      start.setFullYear(end.getFullYear() - 1);
      break;
    case 'custom':
      if (customStartDate && customEndDate) {
        return {
          start: new Date(`${customStartDate}T00:00:00`),
          end: new Date(`${customEndDate}T23:59:59`),
        };
      }
      start.setDate(end.getDate() - 30);
      break;
    default:
      start.setDate(end.getDate() - 30);
  }

  start.setHours(0, 0, 0, 0);
  return { start, end };
};

const getPreviousWindow = ({ start, end }) => {
  const diff = end.getTime() - start.getTime();
  const previousEnd = new Date(start.getTime() - 1);
  const previousStart = new Date(previousEnd.getTime() - diff);
  return { start: previousStart, end: previousEnd };
};

const filterByWindow = (items, dateField, window) =>
  items.filter((item) => {
    const date = toDate(item[dateField]);
    return date && date >= window.start && date <= window.end;
  });

const createComparison = (current, previous, lowerIsBetter = false) => {
  if (!previous) {
    return {
      previous,
      variance: 0,
      isPositive: !lowerIsBetter,
    };
  }

  const variance = (((current - previous) / Math.abs(previous)) * 100).toFixed(1);
  const numericVariance = parseFloat(variance);
  return {
    previous,
    variance: numericVariance,
    isPositive: lowerIsBetter ? numericVariance <= 0 : numericVariance >= 0,
  };
};

const exportReport = (reportType, reportData, comparisonData, window) => {
  const payload = {
    reportType,
    exportedAt: new Date().toISOString(),
    period: {
      start: formatDay(window.start),
      end: formatDay(window.end),
    },
    reportData,
    comparisonData,
  };

  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${reportType}-report-${formatDay(new Date())}.json`;
  link.click();
  URL.revokeObjectURL(url);
};

// ─── Chart sub-components (outside Reports to prevent re-creation) ─────────────
const ReportChart = ({ reportData, currency }) => {
  if (!reportData?.chartData?.length) {
    return (
      <div className="flex h-[300px] items-center justify-center rounded-xl bg-slate-50 text-sm text-slate-500">
        No chartable data in the selected period.
      </div>
    );
  }

  const fmt = (value) => formatCurrency(value, currency);

  if (reportData.chartType === 'line') {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={reportData.chartData}>
          <defs>
            <linearGradient id="rLineGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#2563eb" />
              <stop offset="100%" stopColor="#7c3aed" />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="month" stroke="#94a3b8" tick={{ fontSize: 11 }} />
          <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} />
          <Tooltip formatter={fmt} />
          <Line type="monotone" dataKey="total" stroke="url(#rLineGrad)" strokeWidth={3} dot={{ r: 5, fill: '#2563eb', stroke: '#fff', strokeWidth: 2 }} activeDot={{ r: 7 }} />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (reportData.chartType === 'multiLine') {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={reportData.chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="month" stroke="#94a3b8" tick={{ fontSize: 11 }} />
          <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} />
          <Tooltip formatter={fmt} />
          <Legend />
          <Line type="monotone" dataKey="revenue"  stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} />
          <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={3} dot={{ r: 4 }} />
          <Line type="monotone" dataKey="profit"   stroke="#2563eb" strokeWidth={3} dot={{ r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (reportData.chartType === 'doubleBar') {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={reportData.chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="month" stroke="#94a3b8" tick={{ fontSize: 11 }} />
          <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} />
          <Tooltip formatter={fmt} />
          <Legend />
          <Bar dataKey="inflow"  fill="#10b981" radius={[8, 8, 0, 0]} maxBarSize={48} />
          <Bar dataKey="outflow" fill="#ef4444" radius={[8, 8, 0, 0]} maxBarSize={48} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={reportData.chartData}>
        <defs>
          <linearGradient id="rBarGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7c3aed" />
            <stop offset="100%" stopColor="#4f46e5" />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="category" stroke="#94a3b8" tick={{ fontSize: 11 }} />
        <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} />
        <Tooltip formatter={fmt} />
        <Bar dataKey="total" fill="url(#rBarGrad)" radius={[8, 8, 0, 0]} maxBarSize={52} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const PERIOD_PRESETS = [
  { value: '30days',   label: '30 Days'   },
  { value: '90days',   label: '90 Days'   },
  { value: '6months',  label: '6 Months'  },
  { value: '1year',    label: '12 Months' },
  { value: 'custom',   label: 'Custom'    },
];

function Reports() {
  const [currency, setCurrency] = useState(getDefaultCurrency());
  const [loading, setLoading] = useState(false);
  const [activeReport, setActiveReport] = useState('income');
  const [dateRange, setDateRange] = useState('30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showComparison, setShowComparison] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  useEffect(() => {
    const handleCurrencyChange = (e) => setCurrency(e.detail.currency);
    window.addEventListener('currencyChanged', handleCurrencyChange);
    return () => window.removeEventListener('currencyChanged', handleCurrencyChange);
  }, []);

  useEffect(() => {
    loadBaseData();
  }, []);

  const buildReport = useCallback(() => {
    const dateWindow = getDateWindow(dateRange, customStartDate, customEndDate);
    const previousWindow = getPreviousWindow(dateWindow);

    const currentInvoices = filterByWindow(invoices, 'invoice_date', dateWindow);
    const currentExpenses = filterByWindow(expenses, 'date', dateWindow);
    const previousInvoices = filterByWindow(invoices, 'invoice_date', previousWindow);
    const previousExpenses = filterByWindow(expenses, 'date', previousWindow);
    const today = new Date();

    const currentPaidInvoices = currentInvoices.filter((invoice) => invoice.status === 'paid');
    const previousPaidInvoices = previousInvoices.filter((invoice) => invoice.status === 'paid');

    switch (activeReport) {
      case 'income': {
        const pendingInvoices = currentInvoices.filter((invoice) => invoice.status === 'pending');
        const overdueInvoices = currentInvoices.filter((invoice) => {
          const dueDate = toDate(invoice.due_date);
          return dueDate && dueDate < today && invoice.status !== 'paid';
        });
        const totalIncome = currentPaidInvoices.reduce((sum, invoice) => sum + toSafeNumber(invoice.total_amount || invoice.amount), 0);
        const previousIncome = previousPaidInvoices.reduce((sum, invoice) => sum + toSafeNumber(invoice.total_amount || invoice.amount), 0);

        setReportData({
          title: 'Income Report',
          subtitle: 'Revenue performance and collections health',
          chartTitle: 'Monthly Revenue',
          chartType: 'line',
          chartData: buildMonthlySeries(currentPaidInvoices, 'invoice_date', (invoice) => toSafeNumber(invoice.total_amount || invoice.amount)),
          metrics: [
            { label: 'Total Income', value: formatCurrency(totalIncome, currency), tone: 'from-emerald-500 to-teal-500' },
            { label: 'Pending Amount', value: formatCurrency(pendingInvoices.reduce((sum, invoice) => sum + toSafeNumber(invoice.total_amount || invoice.amount), 0), currency), tone: 'from-amber-500 to-orange-500' },
            { label: 'Overdue Amount', value: formatCurrency(overdueInvoices.reduce((sum, invoice) => sum + toSafeNumber(invoice.total_amount || invoice.amount), 0), currency), tone: 'from-rose-500 to-red-500' },
            { label: 'Invoices in Range', value: `${currentInvoices.length}`, tone: 'from-blue-500 to-indigo-500' },
          ],
          detailRows: [
            { label: 'Paid invoices', value: `${currentPaidInvoices.length}` },
            { label: 'Pending invoices', value: `${pendingInvoices.length}` },
            { label: 'Overdue invoices', value: `${overdueInvoices.length}` },
            { label: 'Average paid invoice', value: formatCurrency(currentPaidInvoices.length ? totalIncome / currentPaidInvoices.length : 0, currency) },
          ],
          insights: [
            overdueInvoices.length > 0
              ? `${overdueInvoices.length} invoice${overdueInvoices.length === 1 ? '' : 's'} are already overdue in this period.`
              : 'No overdue invoices in the selected window.',
            pendingInvoices.length > 0
              ? `${pendingInvoices.length} pending invoice${pendingInvoices.length === 1 ? '' : 's'} still need collection follow-up.`
              : 'Collections are clean with no pending invoices in this window.',
          ],
        });
        setComparisonData(showComparison ? createComparison(totalIncome, previousIncome) : null);
        break;
      }

      case 'expense': {
        const totalExpenses = currentExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.amount), 0);
        const totalTax = currentExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.tax_amount), 0);
        const previousTotalExpenses = previousExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.amount), 0);
        const categoryMap = currentExpenses.reduce((acc, expense) => {
          const key = expense.category || 'Other';
          acc[key] = (acc[key] || 0) + toSafeNumber(expense.amount);
          return acc;
        }, {});

        setReportData({
          title: 'Expense Report',
          subtitle: 'Spending patterns by category and volume',
          chartTitle: 'Expense Breakdown',
          chartType: 'bar',
          chartData: Object.entries(categoryMap)
            .map(([category, total]) => ({ category, total }))
            .sort((a, b) => b.total - a.total),
          metrics: [
            { label: 'Total Expenses', value: formatCurrency(totalExpenses, currency), tone: 'from-rose-500 to-orange-500' },
            { label: 'Tax on Expenses', value: formatCurrency(totalTax, currency), tone: 'from-amber-500 to-yellow-500' },
            { label: 'Average Expense', value: formatCurrency(currentExpenses.length ? totalExpenses / currentExpenses.length : 0, currency), tone: 'from-violet-500 to-fuchsia-500' },
            { label: 'Expense Entries', value: `${currentExpenses.length}`, tone: 'from-slate-700 to-slate-900' },
          ],
          detailRows: [
            { label: 'Largest category', value: Object.entries(categoryMap).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A' },
            { label: 'Highest category spend', value: formatCurrency(Object.entries(categoryMap).sort((a, b) => b[1] - a[1])[0]?.[1] || 0, currency) },
            { label: 'Tax ratio', value: `${totalExpenses > 0 ? ((totalTax / totalExpenses) * 100).toFixed(1) : 0}%` },
            { label: 'Categories used', value: `${Object.keys(categoryMap).length}` },
          ],
          insights: [
            currentExpenses.length > 0
              ? `${Object.keys(categoryMap).length} expense categories are active in this time window.`
              : 'No expenses recorded in the selected time window.',
            totalTax > 0
              ? `Tax-bearing expenses contributed ${formatCurrency(totalTax, currency)}.`
              : 'No tax amount has been captured on expenses in this period.',
          ],
        });
        setComparisonData(showComparison ? createComparison(totalExpenses, previousTotalExpenses, true) : null);
        break;
      }

      case 'tax': {
        const totalIncome = currentPaidInvoices.reduce((sum, invoice) => sum + toSafeNumber(invoice.total_amount || invoice.amount), 0);
        const totalExpenses = currentExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.amount), 0);
        const totalTax = currentExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.tax_amount), 0);
        const previousTax = previousExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.tax_amount), 0);
        const netIncome = totalIncome - totalExpenses - totalTax;

        setReportData({
          title: 'Tax Summary',
          subtitle: 'Tax burden against earnings and spend',
          chartTitle: 'Tax Position',
          chartType: 'bar',
          chartData: [
            { category: 'Income', total: totalIncome },
            { category: 'Expenses', total: totalExpenses },
            { category: 'Tax', total: totalTax },
            { category: 'Net Income', total: netIncome },
          ],
          metrics: [
            { label: 'Total Income', value: formatCurrency(totalIncome, currency), tone: 'from-emerald-500 to-teal-500' },
            { label: 'Total Expenses', value: formatCurrency(totalExpenses, currency), tone: 'from-rose-500 to-orange-500' },
            { label: 'Total Tax', value: formatCurrency(totalTax, currency), tone: 'from-amber-500 to-yellow-500' },
            { label: 'Net Income', value: formatCurrency(netIncome, currency), tone: 'from-blue-500 to-indigo-500' },
          ],
          detailRows: [
            { label: 'Tax as % of income', value: `${totalIncome > 0 ? ((totalTax / totalIncome) * 100).toFixed(1) : 0}%` },
            { label: 'Expense tax ratio', value: `${totalExpenses > 0 ? ((totalTax / totalExpenses) * 100).toFixed(1) : 0}%` },
            { label: 'Paid invoices counted', value: `${currentPaidInvoices.length}` },
            { label: 'Expenses counted', value: `${currentExpenses.length}` },
          ],
          insights: [
            totalTax > 0
              ? `Tracked expense tax totals ${formatCurrency(totalTax, currency)} for this report period.`
              : 'No tax-bearing expenses in the selected period.',
            netIncome >= 0
              ? 'Net income remains positive after expenses and tax.'
              : 'Net income is negative after expenses and tax in this window.',
          ],
        });
        setComparisonData(showComparison ? createComparison(totalTax, previousTax, true) : null);
        break;
      }

      case 'profitloss': {
        const revenueByMonth = buildMonthlySeries(currentPaidInvoices, 'invoice_date', (invoice) => toSafeNumber(invoice.total_amount || invoice.amount));
        const expensesByMonth = buildMonthlySeries(currentExpenses, 'date', (expense) => toSafeNumber(expense.amount));
        const monthMap = {};

        revenueByMonth.forEach((item) => {
          monthMap[item.month] = { month: item.month, revenue: item.total, expenses: 0, profit: item.total };
        });
        expensesByMonth.forEach((item) => {
          if (!monthMap[item.month]) {
            monthMap[item.month] = { month: item.month, revenue: 0, expenses: 0, profit: 0 };
          }
          monthMap[item.month].expenses = item.total;
          monthMap[item.month].profit = monthMap[item.month].revenue - item.total;
        });

        const totalIncome = currentPaidInvoices.reduce((sum, invoice) => sum + toSafeNumber(invoice.total_amount || invoice.amount), 0);
        const totalExpenses = currentExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.amount), 0);
        const totalTax = currentExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.tax_amount), 0);
        const netProfit = totalIncome - totalExpenses - totalTax;
        const previousProfit =
          previousPaidInvoices.reduce((sum, invoice) => sum + toSafeNumber(invoice.total_amount || invoice.amount), 0) -
          previousExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.amount), 0) -
          previousExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.tax_amount), 0);

        setReportData({
          title: 'Profit & Loss',
          subtitle: 'Margin performance over the selected period',
          chartTitle: 'Monthly Profit & Loss',
          chartType: 'multiLine',
          chartData: Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month)),
          metrics: [
            { label: 'Total Income', value: formatCurrency(totalIncome, currency), tone: 'from-emerald-500 to-teal-500' },
            { label: 'Total Expenses', value: formatCurrency(totalExpenses, currency), tone: 'from-rose-500 to-orange-500' },
            { label: 'Total Tax', value: formatCurrency(totalTax, currency), tone: 'from-amber-500 to-yellow-500' },
            { label: 'Net Profit', value: formatCurrency(netProfit, currency), tone: 'from-violet-500 to-fuchsia-500' },
          ],
          detailRows: [
            { label: 'Profit margin', value: `${totalIncome > 0 ? ((netProfit / totalIncome) * 100).toFixed(1) : 0}%` },
            { label: 'Expense ratio', value: `${totalIncome > 0 ? ((totalExpenses / totalIncome) * 100).toFixed(1) : 0}%` },
            { label: 'Tax ratio', value: `${totalIncome > 0 ? ((totalTax / totalIncome) * 100).toFixed(1) : 0}%` },
            { label: 'Months represented', value: `${Object.keys(monthMap).length}` },
          ],
          insights: [
            netProfit >= 0
              ? 'The business remained profitable in the selected period.'
              : 'The business ran at a net loss in the selected period.',
            Object.keys(monthMap).length > 1
              ? 'Use the multi-line chart to spot margin compression across months.'
              : 'Add more historical activity to make the trendline more informative.',
          ],
        });
        setComparisonData(showComparison ? createComparison(netProfit, previousProfit) : null);
        break;
      }

      case 'cashflow': {
        const inflowByMonth = buildMonthlySeries(currentPaidInvoices, 'invoice_date', (invoice) => toSafeNumber(invoice.total_amount || invoice.amount));
        const outflowByMonth = buildMonthlySeries(currentExpenses, 'date', (expense) => toSafeNumber(expense.amount));
        const monthMap = {};

        inflowByMonth.forEach((item) => {
          monthMap[item.month] = { month: item.month, inflow: item.total, outflow: 0, net: item.total };
        });
        outflowByMonth.forEach((item) => {
          if (!monthMap[item.month]) {
            monthMap[item.month] = { month: item.month, inflow: 0, outflow: 0, net: 0 };
          }
          monthMap[item.month].outflow = item.total;
          monthMap[item.month].net = monthMap[item.month].inflow - item.total;
        });

        const cashInflow = currentPaidInvoices.reduce((sum, invoice) => sum + toSafeNumber(invoice.total_amount || invoice.amount), 0);
        const cashOutflow = currentExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.amount), 0);
        const netCashFlow = cashInflow - cashOutflow;
        const previousNetCashFlow =
          previousPaidInvoices.reduce((sum, invoice) => sum + toSafeNumber(invoice.total_amount || invoice.amount), 0) -
          previousExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.amount), 0);

        setReportData({
          title: 'Cash Flow',
          subtitle: 'Movement of cash in and out over the period',
          chartTitle: 'Cash Movement by Month',
          chartType: 'doubleBar',
          chartData: Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month)),
          metrics: [
            { label: 'Cash Inflow', value: formatCurrency(cashInflow, currency), tone: 'from-emerald-500 to-teal-500' },
            { label: 'Cash Outflow', value: formatCurrency(cashOutflow, currency), tone: 'from-rose-500 to-orange-500' },
            { label: 'Net Cash Flow', value: formatCurrency(netCashFlow, currency), tone: netCashFlow >= 0 ? 'from-blue-500 to-indigo-500' : 'from-red-600 to-rose-700' },
            { label: 'Paid Invoices', value: `${currentPaidInvoices.length}`, tone: 'from-slate-700 to-slate-900' },
          ],
          detailRows: [
            { label: 'Average inflow', value: formatCurrency(currentPaidInvoices.length ? cashInflow / currentPaidInvoices.length : 0, currency) },
            { label: 'Average outflow', value: formatCurrency(currentExpenses.length ? cashOutflow / currentExpenses.length : 0, currency) },
            { label: 'Expense entries', value: `${currentExpenses.length}` },
            { label: 'Net position', value: netCashFlow >= 0 ? 'Positive' : 'Negative' },
          ],
          insights: [
            netCashFlow >= 0
              ? 'Cash coming in is ahead of cash going out.'
              : 'Cash outflow is outpacing inflow and needs attention.',
            currentExpenses.length > 0
              ? 'Pair this report with overdue invoices to assess liquidity pressure.'
              : 'No recorded expenses make the inflow picture especially strong in this period.',
          ],
        });
        setComparisonData(showComparison ? createComparison(netCashFlow, previousNetCashFlow) : null);
        break;
      }

      case 'balance':
      default: {
        const receivables = currentInvoices
          .filter((invoice) => invoice.status !== 'paid')
          .reduce((sum, invoice) => sum + toSafeNumber(invoice.total_amount || invoice.amount), 0);
        const paidIncome = currentPaidInvoices.reduce((sum, invoice) => sum + toSafeNumber(invoice.total_amount || invoice.amount), 0);
        const liabilities = currentExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.amount), 0);
        const assets = paidIncome + receivables;
        const equity = assets - liabilities;
        const previousEquity =
          previousPaidInvoices.reduce((sum, invoice) => sum + toSafeNumber(invoice.total_amount || invoice.amount), 0) +
          previousInvoices.filter((invoice) => invoice.status !== 'paid').reduce((sum, invoice) => sum + toSafeNumber(invoice.total_amount || invoice.amount), 0) -
          previousExpenses.reduce((sum, expense) => sum + toSafeNumber(expense.amount), 0);

        setReportData({
          title: 'Balance Sheet',
          subtitle: 'Asset, liability, and equity position from current activity',
          chartTitle: 'Balance Composition',
          chartType: 'bar',
          chartData: [
            { category: 'Assets', total: assets },
            { category: 'Liabilities', total: liabilities },
            { category: 'Equity', total: equity },
            { category: 'Receivables', total: receivables },
          ],
          metrics: [
            { label: 'Assets', value: formatCurrency(assets, currency), tone: 'from-emerald-500 to-teal-500' },
            { label: 'Liabilities', value: formatCurrency(liabilities, currency), tone: 'from-rose-500 to-orange-500' },
            { label: 'Equity', value: formatCurrency(equity, currency), tone: 'from-blue-500 to-indigo-500' },
            { label: 'Receivables', value: formatCurrency(receivables, currency), tone: 'from-violet-500 to-fuchsia-500' },
          ],
          detailRows: [
            { label: 'Paid invoices', value: `${currentPaidInvoices.length}` },
            { label: 'Open invoices', value: `${currentInvoices.filter((invoice) => invoice.status !== 'paid').length}` },
            { label: 'Expense liabilities tracked', value: `${currentExpenses.length}` },
            { label: 'Working capital proxy', value: formatCurrency(assets - liabilities, currency) },
          ],
          insights: [
            receivables > 0
              ? `Receivables worth ${formatCurrency(receivables, currency)} are still outstanding.`
              : 'No outstanding receivables in the selected period.',
            equity >= 0
              ? 'Equity remains positive within the selected activity window.'
              : 'Equity is negative for the selected activity window.',
          ],
        });
        setComparisonData(showComparison ? createComparison(equity, previousEquity) : null);
      }
    }
  }, [activeReport, dateRange, customStartDate, customEndDate, showComparison, invoices, expenses, currency]);

  useEffect(() => {
    if (invoices.length === 0 && expenses.length === 0) return;
    buildReport();
  }, [buildReport, invoices, expenses]);

  const loadBaseData = async () => {
    setLoading(true);
    try {
      const [invoiceRes, expenseRes] = await Promise.all([
        invoiceAPI.getAll({ limit: 1000 }),
        expenseAPI.getAll({ limit: 1000 }),
      ]);
      setInvoices(normalizeInvoices(invoiceRes));
      setExpenses(normalizeExpenses(expenseRes));
    } catch (err) {
      console.error('Failed to load base report data', err);
    } finally {
      setLoading(false);
    }
  };

  const activeReportMeta = useMemo(
    () => reportTypes.find((r) => r.key === activeReport) || reportTypes[0],
    [activeReport]
  );
  const dateWindow = useMemo(
    () => getDateWindow(dateRange, customStartDate, customEndDate),
    [dateRange, customStartDate, customEndDate]
  );
  const ActiveIcon = activeReportMeta.icon;

  return (
    <Layout pageTitle="Reports Studio" pageSubtitle="Focused financial reports with period comparison.">
      <div className="app-page-shell space-y-6">

        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <div className={`overflow-hidden rounded-[22px] bg-gradient-to-br ${activeReportMeta.accent} p-7 text-white shadow-2xl`}>
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: 'radial-gradient(circle at top left, rgba(255,255,255,0.16) 0%, transparent 50%)' }}
          />
          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-[0.64rem] font-bold uppercase tracking-[0.22em] text-white/70">Reports Studio</p>
              <h2 className="mt-2 text-3xl font-extrabold tracking-tight">{activeReportMeta.title}</h2>
              <p className="mt-1.5 max-w-xl text-sm text-white/80">{activeReportMeta.description}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold backdrop-blur">
                  {formatDay(dateWindow.start)} &rarr; {formatDay(dateWindow.end)}
                </span>
                {showComparison && (
                  <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold backdrop-blur">
                    Comparison ON
                  </span>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 rounded-2xl bg-white/15 p-5 backdrop-blur">
              <ActiveIcon size={42} className="opacity-90" />
            </div>
          </div>
        </div>

        <div className="sticky top-32 z-20 space-y-4">
          {/* ── Report Type Tabs ──────────────────────────────────────────── */}
          <div className="overflow-x-auto rounded-2xl border border-slate-200/90 bg-white/90 shadow-sm backdrop-blur-xl">
            <div className="flex min-w-max gap-1.5 p-2">
              {reportTypes.map((report) => {
                const Icon = report.icon;
                const isActive = report.key === activeReport;
                return (
                  <button
                    key={report.key}
                    onClick={() => setActiveReport(report.key)}
                    className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                      isActive
                        ? `bg-gradient-to-r ${report.accent} text-white shadow-md`
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <Icon size={14} />
                    {report.title}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Controls Bar ─────────────────────────────────────────────── */}
          <div className="rounded-2xl border border-slate-200/90 bg-white/90 p-4 shadow-sm backdrop-blur-xl">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex overflow-hidden rounded-xl border border-slate-200 shadow-sm">
                {PERIOD_PRESETS.map((opt, i) => (
                  <button
                    key={opt.value}
                    onClick={() => setDateRange(opt.value)}
                    className={`px-4 py-2 text-sm transition ${
                      dateRange === opt.value
                        ? 'bg-blue-600 font-bold text-white'
                        : 'bg-white font-semibold text-slate-600 hover:bg-slate-50'
                    } ${i > 0 ? 'border-l border-slate-200' : ''}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {dateRange === 'custom' && (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  <span className="text-slate-400">→</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
              )}
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={() => setShowComparison((prev) => !prev)}
                  className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                    showComparison
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md'
                      : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <CalendarRange size={14} />
                  {showComparison ? 'Compare: On' : 'Compare: Off'}
                </button>
                <button
                  onClick={() => exportReport(activeReport, reportData, comparisonData, dateWindow)}
                  disabled={!reportData}
                  className="btn-primary flex items-center gap-2 px-4 py-2 text-sm"
                >
                  <Download size={14} />
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Loading ──────────────────────────────────────────────────── */}
        {loading && (
          <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 shadow-sm">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
          </div>
        )}

        {/* ── Comparison Row ───────────────────────────────────────────── */}
        {!loading && reportData && comparisonData && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="app-page-eyebrow mb-2">Previous Period</p>
              <p className="text-2xl font-extrabold text-slate-900">
                {formatCurrency(comparisonData.previous || 0, currency)}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="app-page-eyebrow mb-2">Period Variance</p>
              <div className="flex items-center gap-2">
                {comparisonData.isPositive
                  ? <ArrowUpRight className="text-emerald-600" size={22} />
                  : <ArrowDownRight className="text-rose-600" size={22} />
                }
                <p className={`text-2xl font-extrabold ${comparisonData.isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {comparisonData.variance}%
                </p>
              </div>
            </div>
            <div className={`rounded-2xl border p-5 shadow-sm ${comparisonData.isPositive ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
              <p className="app-page-eyebrow mb-2">Direction</p>
              <p className={`text-2xl font-extrabold ${comparisonData.isPositive ? 'text-emerald-700' : 'text-rose-700'}`}>
                {comparisonData.isPositive ? '↑ Improving' : '↓ Needs Attention'}
              </p>
            </div>
          </div>
        )}

        {/* ── Report Content ───────────────────────────────────────────── */}
        {!loading && reportData && (
          <>
            {/* KPI Metric Cards */}
            <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
              {reportData.metrics.map((metric) => (
                <div key={metric.label} className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${metric.tone} p-5 text-white shadow-lg`}>
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{ background: 'radial-gradient(circle at top right, rgba(255,255,255,0.18) 0%, transparent 60%)' }}
                  />
                  <div className="relative overflow-hidden">
                    <p className="text-[0.65rem] font-bold uppercase tracking-[0.16em] opacity-80">{metric.label}</p>
                    <p className="mt-2 truncate text-lg font-extrabold leading-tight tracking-tight">{metric.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Chart + Key Details */}
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr),300px]">
              <div className="rounded-2xl bg-white p-6 shadow-md">
                <p className="app-page-eyebrow mb-1">{activeReportMeta.title}</p>
                <h3 className="mb-1 text-lg font-bold text-slate-900">{reportData.chartTitle}</h3>
                <p className="mb-5 text-sm text-slate-500">{reportData.subtitle}</p>
                <ReportChart reportData={reportData} currency={currency} />
              </div>

              <div className="rounded-2xl bg-white p-6 shadow-md">
                <p className="app-page-eyebrow mb-1">Breakdown</p>
                <h3 className="mb-4 text-lg font-bold text-slate-900">Key Details</h3>
                <div className="divide-y divide-slate-100">
                  {reportData.detailRows.map((row) => (
                    <div
                      key={row.label}
                      className="flex items-center justify-between py-3.5"
                    >
                      <span className="text-sm text-slate-500">{row.label}</span>
                      <span className="text-sm font-bold text-slate-900">{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Analyst Notes */}
            <div className="rounded-2xl bg-white p-6 shadow-md">
              <p className="app-page-eyebrow mb-1">Analysis</p>
              <h3 className="mb-5 text-lg font-bold text-slate-900">Analyst Notes</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {reportData.insights.map((insight, i) => (
                  <div
                    key={insight}
                    className={`rounded-xl border-l-4 p-4 text-sm leading-relaxed ${
                      i % 2 === 0
                        ? 'border-blue-400 bg-blue-50 text-blue-800'
                        : 'border-violet-400 bg-violet-50 text-violet-800'
                    }`}
                  >
                    {insight}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

      </div>
    </Layout>
  );
}

export default Reports;
